import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import type { AdminBuildSkipAction, IAdminBuilderQueueBuild } from '@/entities/admin-builder-queue.entity';
import { useState } from 'react';

type AdminBuildSkipDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	action: AdminBuildSkipAction | null;
	build: IAdminBuilderQueueBuild | null;
	onConfirm: (reason: string) => Promise<void>;
	loading?: boolean;
};

const ACTION_COPY: Record<
	AdminBuildSkipAction,
	{ title: string; description: string; confirmLabel: string }
> = {
	single: {
		title: 'Скипнуть билд',
		description: 'Билд будет отменён. Пользователю придёт сообщение в Telegram с указанной причиной.',
		confirmLabel: 'Скипнуть билд',
	},
	current: {
		title: 'Скипнуть текущую сборку',
		description: 'Активная сборка на билдере будет остановлена. Пользователю придёт сообщение в Telegram с причиной.',
		confirmLabel: 'Скипнуть текущий',
	},
	all: {
		title: 'Скипнуть всё',
		description: 'Будут отменены текущая сборка и все билды в очереди. Каждому владельцу придёт сообщение в Telegram с причиной.',
		confirmLabel: 'Скипнуть всё',
	},
};

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 500;

export default function AdminBuildSkipDialog({
	open,
	onOpenChange,
	action,
	build,
	onConfirm,
	loading = false,
}: AdminBuildSkipDialogProps) {
	const [reason, setReason] = useState('');

	if (!action) {
		return null;
	}

	const copy = ACTION_COPY[action];
	const trimmedReason = reason.trim();
	const canSubmit = trimmedReason.length >= MIN_REASON_LENGTH && trimmedReason.length <= MAX_REASON_LENGTH;

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			setReason('');
		}
		onOpenChange(nextOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-[480px]'>
				<DialogHeader>
					<DialogTitle>{copy.title}</DialogTitle>
					<DialogDescription>{copy.description}</DialogDescription>
				</DialogHeader>

				{build ? (
					<div className='rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm space-y-1'>
						<p className='font-medium truncate'>{build.appName || build.id}</p>
						<p className='text-xs text-muted-foreground'>@{build.profileNickname}</p>
						<p className='text-xs text-muted-foreground font-mono break-all'>{build.id}</p>
					</div>
				) : null}

				<div className='space-y-2'>
					<label className='text-sm font-medium' htmlFor='admin-build-skip-reason'>
						Причина (обязательно)
					</label>
					<Textarea
						id='admin-build-skip-reason'
						value={reason}
						onChange={event => setReason(event.target.value)}
						placeholder='Укажите причину остановки для пользователя'
						rows={4}
						maxLength={MAX_REASON_LENGTH}
					/>
					<p className='text-xs text-muted-foreground'>
						{trimmedReason.length}/{MAX_REASON_LENGTH} · минимум {MIN_REASON_LENGTH} символа
					</p>
				</div>

				<DialogFooter className='gap-2 sm:gap-0'>
					<Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={loading}>
						Отмена
					</Button>
					<Button
						type='button'
						variant='destructive'
						disabled={!canSubmit || loading}
						onClick={() => void onConfirm(trimmedReason)}
					>
						{copy.confirmLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
